import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserGroup, faQrcode, faDownload, faSearch,
  faCheckCircle, faTimesCircle, faXmark, faHistory,
  faBuilding, faArrowLeft, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../utils/constants';
import useAutoDismissSnack from '../../Hooks/AutoDismissSnack';

const BASE = `${BASE_URL}`;

const TIPO_LABELS = { FAMILIAR: 'Familiar', EMPLEADO: 'Empleado', OTRO: 'Otro' };
const TIPO_COLORS = {
  FAMILIAR: 'bg-blue-100 text-blue-800',
  EMPLEADO: 'bg-purple-100 text-purple-800',
  OTRO: 'bg-orange-100 text-orange-800',
};

function AdminRecurringVisitors() {
  const navigate = useNavigate();
  const [towers, setTowers] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [selectedTower, setSelectedTower] = useState(null); // null = tower selection view
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // QR modal
  const [qrOpen, setQrOpen] = useState(false);
  const [qrVisitor, setQrVisitor] = useState(null);
  const qrRef = useRef();

  // History modal
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyVisitor, setHistoryVisitor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState('');
  const [snackMessage, setSnackMessage] = useState('');
  useAutoDismissSnack(openSnack, setOpenSnack);

  const snack = (status, msg) => { setSnackStatus(status); setSnackMessage(msg); setOpenSnack(true); };

  useEffect(() => {
    Promise.all([
      axios.get(`${BASE}/towers`),
      axios.get(`${BASE}/visitantesRecurrentes`),
    ])
      .then(([towersRes, visitorsRes]) => {
        setTowers(towersRes.data);
        setVisitors(visitorsRes.data);
      })
      .catch(() => snack('error', 'Error al cargar los datos'))
      .finally(() => setLoading(false));
  }, []);

  // Count visitors per tower
  const countForTower = (id_torre) =>
    visitors.filter(v => v.id_torre === id_torre).length;

  // Visitors shown in table: filter by selected tower + search
  const tableVisitors = visitors.filter(v => {
    const matchesTower = selectedTower ? v.id_torre === selectedTower.id_torre : false;
    if (!matchesTower) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      `${v.nombre} ${v.apellido}`.toLowerCase().includes(q) ||
      (v.identificacion || '').toLowerCase().includes(q) ||
      (v.num_apartamento || '').toString().toLowerCase().includes(q) ||
      TIPO_LABELS[v.tipo]?.toLowerCase().includes(q) ||
      (v.usuario_nombre + ' ' + v.usuario_apellido).toLowerCase().includes(q)
    );
  });

  const handleDownloadQr = async () => {
    if (!qrRef.current) return;
    try {
      const canvas = await html2canvas(qrRef.current, { backgroundColor: '#fff', scale: 3 });
      const link = document.createElement('a');
      link.download = `QR-${qrVisitor.nombre}-${qrVisitor.apellido}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch { snack('error', 'Error al descargar QR'); }
  };

  const openHistoryFor = async (v) => {
    setHistoryVisitor(v);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${BASE}/visitantesRecurrentes/${v.id}/ingresos`);
      setHistoryList(res.data);
    } catch { setHistoryList([]); }
    finally { setHistoryLoading(false); }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    return new Date(str).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <>
      <Navigation />

      {/* Snackbar */}
      {openSnack && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white z-50 flex items-center gap-2`}>
          <FontAwesomeIcon icon={snackStatus === 'success' ? faCheckCircle : faTimesCircle} className="h-5 w-5" />
          <span className="font-semibold">{snackMessage}</span>
          <button onClick={() => setOpenSnack(false)} className="ml-2 hover:bg-white/20 rounded p-1">
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (selectedTower) { setSelectedTower(null); setSearch(''); }
                  else navigate('/dashboard');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
                {selectedTower ? 'Torres' : 'Volver'}
              </button>

              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shrink-0">
                  <FontAwesomeIcon icon={faUserGroup} className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800 leading-tight">
                    Visitantes Recurrentes
                    {selectedTower && (
                      <span className="text-indigo-600"> — {selectedTower.nombre}</span>
                    )}
                  </h1>
                  {/* Breadcrumb */}
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    <span>Administración</span>
                    <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5" />
                    <span>Visitantes Recurrentes</span>
                    {selectedTower && (
                      <>
                        <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5" />
                        <span>{selectedTower.nombre}</span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Search — only visible when a tower is selected */}
              {selectedTower && (
                <div className="relative w-full sm:w-64">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent text-sm"
                    placeholder="Buscar visitante, apto, tipo..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
            </div>
          ) : !selectedTower ? (
            /* ── TOWER SELECTION VIEW ── */
            <>
              <p className="text-sm text-gray-500 mb-4 text-center">Selecciona una torre para ver sus visitantes recurrentes</p>
              {towers.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl shadow">
                  <FontAwesomeIcon icon={faBuilding} className="h-16 w-16 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-400">No hay torres registradas</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {towers.map(tower => {
                    const count = countForTower(tower.id_torre);
                    return (
                      <button
                        key={tower.id_torre}
                        onClick={() => { setSelectedTower(tower); setSearch(''); }}
                        className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left border-2 border-transparent hover:border-indigo-400 focus:outline-none focus:border-indigo-400"
                      >
                        <div className="bg-gradient-to-br from-indigo-500 to-blue-600 p-8 flex flex-col items-center">
                          <FontAwesomeIcon icon={faBuilding} className="h-14 w-14 text-white mb-3 group-hover:scale-110 transition-transform" />
                          <h2 className="text-xl font-bold text-white text-center">{tower.nombre}</h2>
                        </div>
                        <div className="p-5 text-center">
                          <p className="text-3xl font-black text-indigo-600">{count}</p>
                          <p className="text-sm text-gray-500 mt-0.5">
                            visitante{count !== 1 ? 's' : ''} recurrente{count !== 1 ? 's' : ''}
                          </p>
                          <div className="mt-4 flex items-center justify-center gap-1.5 text-indigo-500 text-sm font-semibold group-hover:gap-2.5 transition-all">
                            <span>Ver visitantes</span>
                            <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* ── VISITORS TABLE VIEW ── */
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {tableVisitors.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                  <FontAwesomeIcon icon={faUserGroup} className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-lg font-semibold">
                    {search ? 'Sin resultados para la búsqueda' : 'No hay visitantes recurrentes en esta torre'}
                  </p>
                  {search && (
                    <button onClick={() => setSearch('')} className="mt-3 text-indigo-500 text-sm hover:underline">
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="px-5 py-3 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between">
                    <p className="text-sm text-indigo-700 font-medium">
                      {tableVisitors.length} visitante{tableVisitors.length !== 1 ? 's' : ''} encontrado{tableVisitors.length !== 1 ? 's' : ''}
                      {search && <span className="text-indigo-400"> · filtrando por "{search}"</span>}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gradient-to-r from-indigo-500 to-blue-600 text-white">
                          <th className="px-4 py-3 text-left font-semibold">Visitante</th>
                          <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                          <th className="px-4 py-3 text-left font-semibold">Identificación</th>
                          <th className="px-4 py-3 text-left font-semibold">Propietario</th>
                          <th className="px-4 py-3 text-left font-semibold">Apartamento</th>
                          <th className="px-4 py-3 text-center font-semibold">Ingresos</th>
                          <th className="px-4 py-3 text-left font-semibold">Último ingreso</th>
                          <th className="px-4 py-3 text-center font-semibold">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {tableVisitors.map(v => (
                          <tr key={v.id} className="hover:bg-indigo-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-800">{v.nombre} {v.apellido}</div>
                              {v.email && <div className="text-xs text-gray-500">{v.email}</div>}
                              {v.telefono && <div className="text-xs text-gray-500">{v.telefono}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${TIPO_COLORS[v.tipo]}`}>
                                {v.tipo === 'OTRO' && v.tipo_personalizado ? v.tipo_personalizado : TIPO_LABELS[v.tipo] || v.tipo}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">{v.identificacion || '-'}</td>
                            <td className="px-4 py-3 text-gray-700">{v.usuario_nombre} {v.usuario_apellido}</td>
                            <td className="px-4 py-3 text-gray-700">{v.num_apartamento}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-bold">
                                {v.total_ingresos}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(v.ultimo_ingreso)}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => { setQrVisitor(v); setQrOpen(true); }}
                                  className="flex items-center gap-1 px-2 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition-colors"
                                  title="Ver QR"
                                >
                                  <FontAwesomeIcon icon={faQrcode} className="h-3.5 w-3.5" />
                                  QR
                                </button>
                                <button
                                  onClick={() => openHistoryFor(v)}
                                  className="flex items-center gap-1 px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-xs font-semibold transition-colors"
                                  title="Ver historial"
                                >
                                  <FontAwesomeIcon icon={faHistory} className="h-3.5 w-3.5" />
                                  Historial
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* QR Modal */}
      {qrOpen && qrVisitor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">{qrVisitor.nombre} {qrVisitor.apellido}</h2>
              <button onClick={() => setQrOpen(false)} className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <div ref={qrRef} className="bg-white p-4 rounded-xl border border-gray-200 flex flex-col items-center gap-2">
                <QRCodeSVG value={qrVisitor.token_qr} size={200} level="H" includeMargin />
                <div className="text-center">
                  <p className="font-bold text-gray-800 text-sm">{qrVisitor.nombre} {qrVisitor.apellido}</p>
                  <p className="text-xs text-gray-500">{TIPO_LABELS[qrVisitor.tipo] || qrVisitor.tipo}</p>
                </div>
                <div className="mt-1 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                  <p className="text-xs text-gray-500 text-center leading-none mb-0.5">Código de acceso</p>
                  <p className="text-xl font-black tracking-widest text-indigo-700 text-center">{qrVisitor.token_qr}</p>
                </div>
              </div>
              <div className="text-center text-xs text-gray-500 px-2">
                <p className="font-semibold text-gray-700">Propietario: {qrVisitor.usuario_nombre} {qrVisitor.usuario_apellido}</p>
                <p>Apto {qrVisitor.num_apartamento} · {qrVisitor.torre_nombre}</p>
              </div>
              <button
                onClick={handleDownloadQr}
                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-indigo-500 to-blue-600 text-white rounded-lg font-semibold text-sm hover:shadow-md transition-all"
              >
                <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                Descargar QR
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyOpen && historyVisitor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-lg font-bold text-white">
                Historial — {historyVisitor.nombre} {historyVisitor.apellido}
              </h2>
              <button onClick={() => setHistoryOpen(false)} className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {historyLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
                </div>
              ) : historyList.length === 0 ? (
                <p className="text-center text-gray-400 py-10">Sin ingresos registrados</p>
              ) : (
                <ul className="space-y-2">
                  {historyList.map((entry, i) => (
                    <li key={entry.id} className="flex items-start gap-3 bg-gray-50 rounded-xl p-3">
                      <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{formatDate(entry.fecha_ingreso)}</p>
                        {entry.observacion && <p className="text-xs text-gray-500 mt-0.5">{entry.observacion}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminRecurringVisitors;

